import { useCallback, useMemo, useState } from "react";
import Notify from "config/notify";
import { useNavigate } from "react-router";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { setSessionData } from "util/session_action";
import getWheelDetailByCodeApi from "features/mst_wheel/api/wheelCodeSearchApi";
import { getTireDetailByProductCodeApi } from "features/car_model/api/carModelApi";
import {
  CombinationType,
  IProductSearchEntry,
  ISearchResult,
} from "features/mst_wheel/types/wheel-code";
import {
  SetWheelProcFeeStorage,
  SetTireProcFeeStorage,
} from "features/mst_proc_fee/types/proc-fee";
import { EstQuoteDetailTire, ITireItemDetail } from "features/car_model/types";
import { MstItemDetailWheelDetail } from "features/mst_wheel/types";

/**
 * Map mst_proc_fee from API to proc_fees format for SetTireProcFeeStorage
 * @param mstProcFee - The mst_proc_fee object from API response
 * @returns Mapped proc_fees object
 */
const mapProcFees = (mstProcFee: any): any => {
  if (!mstProcFee) {
    return {
      replac_labor_cost: { value: undefined, checked: true },
      waste_tire_cost: { value: undefined, checked: true },
      rubber_stem_valve_cost: { value: undefined, checked: true },
      nitrogen_inflation_cost: { value: undefined, checked: false },
      hub_rust_prevention_cost: { value: undefined, checked: false },
    };
  }

  return {
    replac_labor_cost: { value: mstProcFee.replac_labor_cost, checked: true },
    waste_tire_cost: { value: mstProcFee.waste_tire_cost, checked: true },
    rubber_stem_valve_cost: { value: mstProcFee.rubber_stem_valve_cost, checked: true },
    nitrogen_inflation_cost: { value: mstProcFee.nitrogen_inflation_cost, checked: false },
    hub_rust_prevention_cost: { value: mstProcFee.hub_rust_prevention_cost, checked: false },
  };
};

/**
 * Map TireDetailSearch to EstQuoteDetailTire with original tire data
 * @param tire - The tire data from search API
 * @returns Mapped EstQuoteDetailTire object with original tire data
 */
const mapTireToQuoteDetail = (tire: ITireItemDetail): EstQuoteDetailTire => ({
  tire_item_cd: tire.item_cd || "",
  maker_name: tire.maker_name || "",
  item_name: tire.item_name || "",
  tire_loin_speed_mark: tire.tire_loin_speed_mark || "",
  tire_label: tire.tire_label || "",
  quantity: 4,
  nomal_price: tire.list_price || 0,
  fixed_discount: tire.fixed_discount || 0,
  special_discount: tire.special_discount || 0,
  discounted_sale_price: tire.list_price || 0,
  wage_cost_price: 0,
  wage_cost_in_price: 0,
  puncture_protection_price: 0,
  puncture_protection_in_price: 0,
  cb_tire_price: tire.cb_tire_price || 0,
  cb_wheel_price: tire.cb_wheel_price || 0,
  confirm_total_price: tire.list_price || 0,
  remarks: tire.remarks || "",
  checked: true,
  // Preserve original tire data for calculation (keep entire object)
  originalTireData: tire,
});

/**
 * Map MstItemDetailWheel to MstItemDetailWheelDetail with default pricing
 * @param wheel - The wheel data from search API
 * @returns Mapped MstItemDetailWheelDetail object
 */
const mapWheelToWheelDetail = (wheel: any): MstItemDetailWheelDetail => ({
  ...wheel,
  regular_price: wheel.regular_price || 0,
  discounted_price: wheel.regular_price || 0,
  final_price: wheel.regular_price || 0,
});

/**
 * Custom React hook for managing tire and wheel code search logic.
 * Provides state and functions to search for tire and wheel details, handle loading and error states,
 * and manage search results for both tire and wheel entries.
 */
export default function useWheelCode() {
  const [searchEntryFirstWheel, setSearchEntryFirstWheel] = useState<IProductSearchEntry>({
    tires: [],
    wheels: [],
  });
  const [searchEntryFirstTire, setSearchEntryFirstTire] = useState<IProductSearchEntry>({
    tires: [],
    wheels: [],
  });
  // Store mst_proc_fee for tire searches
  const [tireProcFeeFirstWheel, setTireProcFeeFirstWheel] = useState<any>(null);
  const [tireProcFeeFirstTire, setTireProcFeeFirstTire] = useState<any>(null);
  const navigate = useNavigate();

  // Function to fetch tire information based on search codes
  const handleTireSearch = useCallback(
    async (item_cd: string, combineType: CombinationType): Promise<ISearchResult> => {
      try {
        const tireResponse = await getTireDetailByProductCodeApi({ item_cd });
        if (tireResponse?.list?.mst_item_detail_tire?.length > 0) {
          const tire = tireResponse.list.mst_item_detail_tire[0];

          // Save mst_proc_fee to state based on combineType
          if (combineType === CombinationType.TireWithWheels) {
            setTireProcFeeFirstTire(tireResponse.list.mst_proc_fee);
          } else {
            setTireProcFeeFirstWheel(tireResponse.list.mst_proc_fee);
          }

          // Matching check: compare tire with existing wheels
          let isMismatch = false;
          let baseWheelRim;

          if (combineType === CombinationType.WheelWithTires) {
            // Check against existing wheel in WheelWithTires combination
            baseWheelRim = searchEntryFirstWheel.wheels[0]?.wheel_rim_diam;
          } else if (combineType === CombinationType.TireWithWheels) {
            // Check against existing wheels in TireWithWheels combination
            baseWheelRim = searchEntryFirstTire.wheels[0]?.wheel_rim_diam;
          }

          if (baseWheelRim !== undefined && baseWheelRim !== null) {
            isMismatch = Number(tire.tire_rim_diam) !== Number(baseWheelRim);
          }

          if (isMismatch) {
            Notify.setNotification({
              show: true,
              title: "タイヤサイズがホイールと異なります",
            });
            // Return payload with mismatch flag for UI to highlight input
            return {
              productCode: item_cd,
              productName: tire.item_name,
              size: `${tire.tire_width}/${tire.tire_aspect_ratio}R${tire.tire_rim_diam}`,
              loadIndex: tire.tire_loin_speed_mark,
              rimDiam: tire.tire_rim_diam,
              mismatch: true,
              combineType,
            };
          }

          if (combineType === CombinationType.TireWithWheels)
            setSearchEntryFirstTire((prev) => ({
              ...prev,
              tires: [tire],
            }));
          else
            setSearchEntryFirstWheel((prev) => ({
              ...prev,
              tires: [...prev.tires, tire],
            }));

          // Return tire data in the requested format (Omit<TireData, "isSearchMode">)

          return {
            productCode: item_cd,
            productName: tire.item_name,
            size: `${tire.tire_width}/${tire.tire_aspect_ratio}R${tire.tire_rim_diam}`,
            loadIndex: tire.tire_loin_speed_mark,
            rimDiam: tire.tire_rim_diam,
          };
        }
        // Return default tire data if no data found
        Notify.setNotification({
          show: true,
          title: "商品が選択されていません",
        });

        return {
          productCode: item_cd,
          productName: "",
          size: "",
          loadIndex: "",
          rimDiam: null,
        };
      } catch (e) {
        // swallow error and return default to keep UI responsive
        // Return default tire data on error
        return {
          productCode: item_cd,
          productName: "",
          size: "",
          loadIndex: "",
          rimDiam: null,
        };
      }
    },
    [searchEntryFirstWheel, searchEntryFirstTire],
  );

  // Function to fetch wheel information based on search codes
  const handleWheelSearch = useCallback(
    async (item_cd: string, combineType: CombinationType): Promise<ISearchResult> => {
      try {
        const wheelResponse = await getWheelDetailByCodeApi({ item_cd });

        // Check if wheel data exists
        if (wheelResponse?.list?.mst_item_detail_wheel?.length > 0) {
          const wheel = wheelResponse.list.mst_item_detail_wheel[0];

          // Matching check: compare wheel with existing tires
          let isMismatch = false;
          let baseTireRim;

          if (combineType === CombinationType.WheelWithTires) {
            // Check against existing tires in WheelWithTires combination
            baseTireRim = searchEntryFirstWheel.tires[0]?.tire_rim_diam;
          } else if (combineType === CombinationType.TireWithWheels) {
            // Check against existing tire in TireWithWheels combination
            baseTireRim = searchEntryFirstTire.tires[0]?.tire_rim_diam;
          }

          if (baseTireRim !== undefined && baseTireRim !== null) {
            isMismatch = Number(wheel.wheel_rim_diam) !== Number(baseTireRim);
          }

          if (isMismatch) {
            Notify.setNotification({
              show: true,
              title: "タイヤサイズがホイールと異なります",
            });
            // Return payload with mismatch flag for UI to highlight input
            return {
              productCode: item_cd,
              productName: wheel.item_name || "",
              size: wheel.wheel_size || "",
              loadIndex: "",
              rimDiam: wheel.wheel_rim_diam,
              mismatch: true,
              combineType,
            };
          }

          if (combineType === CombinationType.WheelWithTires)
            setSearchEntryFirstWheel((prev) => ({
              ...prev,
              wheels: [wheel],
            }));
          else
            setSearchEntryFirstTire((prev) => ({
              ...prev,
              wheels: [...prev.wheels, wheel],
            }));
          return {
            productCode: item_cd,
            productName: wheel.item_name || "",
            size: wheel.wheel_size || "",
            loadIndex: "",
            rimDiam: wheel.wheel_rim_diam,
          };
        }

        Notify.setNotification({
          show: true,
          title: "商品が選択されていません",
        });

        // Return default wheel data if no data found
        return {
          productCode: item_cd,
          productName: "",
          size: "",
          loadIndex: "",
          rimDiam: null,
        };
      } catch (e) {
        // swallow error and return default to keep UI responsive
        // Return default wheel data on error
        return {
          productCode: item_cd,
          productName: "",
          size: "",
          loadIndex: "",
          rimDiam: null,
        };
      }
    },
    [searchEntryFirstTire],
  );

  // Function to remove tire item based on CombinationType
  const removeTireItem = useCallback((productCode: string, combineType: CombinationType) => {
    if (combineType === CombinationType.TireWithWheels) {
      // Remove from searchEntryFirstTire
      setSearchEntryFirstTire((prev) => {
        const filtered = prev.tires.filter((tire) => tire.item_cd !== productCode);
        return {
          ...prev,
          tires: filtered,
        };
      });
    } else {
      // Remove from searchEntryFirstWheel
      setSearchEntryFirstWheel((prev) => {
        const filtered = prev.tires.filter((tire) => tire.item_cd !== productCode);
        return {
          ...prev,
          tires: filtered,
        };
      });
    }
  }, []);

  // Function to remove wheel item based on CombinationType
  const removeWheelItem = useCallback((productCode: string, combineType: CombinationType) => {
    if (combineType === CombinationType.WheelWithTires) {
      // Remove from searchEntryFirstWheel
      setSearchEntryFirstWheel((prev) => {
        const filtered = prev.wheels.filter((wheel) => wheel.item_cd !== productCode);
        return {
          ...prev,
          wheels: filtered,
        };
      });
    } else {
      // Remove from searchEntryFirstTire
      setSearchEntryFirstTire((prev) => {
        const filtered = prev.wheels.filter((wheel) => wheel.item_cd !== productCode);
        return {
          ...prev,
          wheels: filtered,
        };
      });
    }
  }, []);

  const handleBackToQuoteList = useCallback(() => navigate(routes.quoteList), [navigate]);

  const onHandleNavigate = useCallback(
    (combineType: CombinationType) => {
      // Check data based on combineType
      if (combineType === CombinationType.WheelWithTires) {
        sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_FROM, routes.mstCarWheelProductCode);
        if (searchEntryFirstWheel.wheels.length === 0 || searchEntryFirstWheel.tires.length === 0) {
          Notify.setNotification({
            show: true,
            title: "商品が選択されていません",
          });
          return;
        }

        // Map and save wheel data to session
        const wheelsDetail: MstItemDetailWheelDetail[] =
          searchEntryFirstWheel.wheels.map(mapWheelToWheelDetail);
        const wheelStorage: SetWheelProcFeeStorage = {
          wheels_detail: wheelsDetail,
          cost_setting: {
            replac_labor_cost: "",
            waste_tire_cost_flag: true,
            waste_tire_cost: "",
            rubber_stem_valve_flag: true,
            rubber_stem_valve_cost: "",
            nitrogen_inflation_flag: false,
            nitrogen_inflation_cost: "",
            hub_rust_prevention_flag: false,
            hub_rust_prevention_cost: "",
            tire_puncture_flag: 0,
            cashback_flag: false,
            cashback_period: 1,
            rounding: 0,
            take_out: "0",
            take_out_flag: true,
            nut: "",
            nut_flag: true,
            lock_nut: "",
            lock_nut_flag: false,
            hub_ring: "",
            hub_ring_flag: false,
            wheel_coating: "",
            wheel_coating_flag: false,
          },
          discount_rate: wheelsDetail.map((wheel) => ({
            id: wheel.id,
            value: "0",
            isError: false,
          })),
          cost_price_map: {},
        };
        setSessionData(SESSION_KEY.SELECTED_WHEEL, wheelStorage);

        // Map and save tire data to session
        const tiresDetail: EstQuoteDetailTire[] =
          searchEntryFirstWheel.tires.map(mapTireToQuoteDetail);
        const tireStorage: SetTireProcFeeStorage = {
          tbl_quote_detail_tire: tiresDetail,
          cashback: {
            puncture: 0,
            cashback_period: 0,
          },
          proc_fees: mapProcFees(tireProcFeeFirstWheel),
        };
        setSessionData(SESSION_KEY.SELECTED_TIRE, tireStorage);
      } else if (combineType === CombinationType.TireWithWheels) {
        // Check if tire and wheel data exist for TireWithWheels combination
        if (searchEntryFirstTire.tires.length === 0 || searchEntryFirstTire.wheels.length === 0) {
          Notify.setNotification({
            show: true,
            title: "商品が選択されていません",
          });
          return;
        }

        // Map and save wheel data to session
        const wheelsDetail: MstItemDetailWheelDetail[] =
          searchEntryFirstTire.wheels.map(mapWheelToWheelDetail);
        const wheelStorage: SetWheelProcFeeStorage = {
          wheels_detail: wheelsDetail,
          cost_setting: {
            replac_labor_cost: "",
            waste_tire_cost_flag: true,
            waste_tire_cost: "",
            rubber_stem_valve_flag: true,
            rubber_stem_valve_cost: "",
            nitrogen_inflation_flag: false,
            nitrogen_inflation_cost: "",
            hub_rust_prevention_flag: false,
            hub_rust_prevention_cost: "",
            tire_puncture_flag: 0,
            cashback_flag: false,
            cashback_period: 1,
            rounding: 0,
            take_out: "0",
            take_out_flag: true,
            nut: "",
            nut_flag: true,
            lock_nut: "",
            lock_nut_flag: false,
            hub_ring: "",
            hub_ring_flag: false,
            wheel_coating: "",
            wheel_coating_flag: false,
          },
          discount_rate: wheelsDetail.map((wheel) => ({
            id: wheel.id,
            value: "0",
            isError: false,
          })),
          cost_price_map: {},
        };
        setSessionData(SESSION_KEY.SELECTED_WHEEL, wheelStorage);

        // Map and save tire data to session
        const tiresDetail: EstQuoteDetailTire[] =
          searchEntryFirstTire.tires.map(mapTireToQuoteDetail);
        const tireStorage: SetTireProcFeeStorage = {
          tbl_quote_detail_tire: tiresDetail,
          cashback: {
            puncture: 0,
            cashback_period: 0,
          },
          proc_fees: mapProcFees(tireProcFeeFirstTire),
        };
        setSessionData(SESSION_KEY.SELECTED_TIRE, tireStorage);
      }

      sessionStorage.removeItem(SESSION_KEY.NEW_QUOTE_MAKER);
      // If data exists, proceed with navigation
      navigate(routes.mstConfirmProcFee);
    },
    [
      searchEntryFirstWheel,
      searchEntryFirstTire,
      tireProcFeeFirstWheel,
      tireProcFeeFirstTire,
      navigate,
    ],
  );

  return useMemo(
    () => ({
      handleWheelSearch,
      handleTireSearch,
      removeTireItem,
      removeWheelItem,
      handleBackToQuoteList,
      onHandleNavigate,
    }),
    [
      handleWheelSearch,
      handleTireSearch,
      removeTireItem,
      removeWheelItem,
      handleBackToQuoteList,
      onHandleNavigate,
    ],
  );
}
